import { Component, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { CustomerAccountInput } from '../../../api/types';
import {
  SynAlertComponent,
  SynButtonComponent,
  SynCheckboxComponent,
  SynDividerComponent,
  SynergyFormsModule,
  SynIconComponent,
  SynInputComponent,
  SynOptionComponent,
  SynSelectComponent,
  SynTextareaComponent,
  SynValidateComponent,
} from '@synergy-design-system/angular';
import { FormsModule, NgForm } from '@angular/forms';
import { PasswordInputComponent } from '../../components/password-input.component';
import { HelpTextComponent } from '../../components/help-text.component';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-customer-account-form',
  providers: [DatePipe],
  template: `
    <form #form=ngForm (submit)="onSubmit(form)">
      <div class="fields form-width-large">
        <div class="flex-row gap-1">
          <syn-select
            label="Salutation" i18n-label="@@salutation"
            [disabled]="!canEdit()"
            [(ngModel)]="this.account().salutation"
            name="salutation"
            style="width: 8rem"
          >
            <syn-option value="-">-</syn-option>
            <syn-option value="mr" i18n="@@salutation.mr">Mr.</syn-option>
            <syn-option value="ms" i18n="@@salutation.ms">Ms.</syn-option>
          </syn-select>
          <syn-validate on="syn-blur live" style="flex-grow: 1">
            <syn-input
              label="First Name" i18n-label="@@first-name"
              [(ngModel)]="this.account().firstName"
              [disabled]="!canEdit()"
              [required]="true"
              [maxlength]="maxPropertyLength"
              name="firstName"
            />
          </syn-validate>
        </div>
        <syn-validate on="syn-blur live">
          <syn-input
            label="Last Name" i18n-label="@@last-name"
            [(ngModel)]="this.account().lastName"
            [disabled]="!canEdit()"
            [required]="true"
            [maxlength]="maxPropertyLength"
            name="lastName" />
        </syn-validate>
        <syn-validate on="syn-blur live">
          <syn-input
            label="Username" i18n-label="@@username"
            [(ngModel)]="this.username"
            [disabled]="!canEdit()"
            [required]="true"
            [maxlength]="maxPropertyLength"
            name="username" />
        </syn-validate>
        <syn-validate on="syn-blur live">
          <syn-input
            label="Email" i18n-label="@@email"
            [(ngModel)]="this.email"
            [disabled]="!canEdit()"
            [required]="true"
            [maxlength]="maxPropertyLength"
            type="email"
            name="email" />
        </syn-validate>

        <syn-alert variant="warning" [open]="triesToCreateSickEmployee()">
          <syn-icon name="warning_fill" slot="icon" />
          <span i18n="@@warning-create-sick-employee">
            It looks like you are trying to create a SICK employee via the customer manager.
            SICK employees are automatically created when they login for the first time.
            If you create a SICK employee via the customer manager, they will be missing
            important group and role associations.
          </span>
        </syn-alert>

        <div class="flex-row gap-2" style="flex-basis: 100%">
          <syn-checkbox [(ngModel)]="this.account().enabled" name="enabled" [disabled]="!canEdit()">
            <span i18n="@@enabled">Enabled</span>
          </syn-checkbox>
          <syn-checkbox [(ngModel)]="this.account().emailVerified" name="emailVerified" [disabled]="!canEdit()">
            <span i18n="@@email-verified">Email Verified</span>
          </syn-checkbox>
        </div>

        @if (!create()){
          <app-help-text>
            <syn-input
              label="Last Verification Email Sent" i18n-label="@@last-verification-email-sent"
              [value]="lastEmailVerificationTriggeredFormatted()"
              [disabled]="true"
            />
            <span helpText i18n="@@last-email-verification-help">Date on which the user was last asked to verify their email address.
              This does not indicate whether the user actually verified their email address.</span>
          </app-help-text>
          <syn-input
            label="Last Login" i18n-label="@@last-login"
            [value]="lastLoginFormatted()"
            [disabled]="true"
          />
          <syn-input
            label="Created At" i18n-label="@@creation-date"
            [value]="creationTimestampFormatted()"
            [disabled]="true"
          />
        }

        <app-help-text>
          <syn-select
            label="Account Type" i18n-label="@@account-type"
            name="type"
            [(ngModel)]="this.account().type"
          >
            <syn-option value="customer" i18n="@@customer">Customer</syn-option>
            <syn-option value="demo" i18n="@@demo-account">Demo Account</syn-option>
          </syn-select>
          <span helpText i18n="@@account-type-help">
          Choose whether you want to create an account for a customer or for demo purposes.
        </span>
        </app-help-text>

        @if (ssus().length > 0) {
          <app-help-text>
            <syn-validate on="syn-blur live">
              <syn-select #ssuSelect label="SSU Code" i18n-label="@@ssu-code" [(ngModel)]="this.account().ssuCode"
                          [disabled]="!(create()||isAdmin())"
                          [required]="false" name="ssuCode">
                <syn-option value="">-</syn-option>
                <syn-divider />
                @for (ssu of ssus(); track ssu) {
                  <syn-option [value]="ssu">{{ ssu }}</syn-option>
                }
              </syn-select>
            </syn-validate>
            <span helpText i18n="@@ssu-help">
            Assign an SSU code to this account.
          </span>
          </app-help-text>
        }

        <app-help-text>
          <syn-textarea
            label="Notes" i18n-label="@@notes"
            [rows]="4"
            [maxlength]="500"
            resize="none"
            [spellcheck]="false"
            [(ngModel)]="this.account().note"
            name="note"
          />
          <span helpText i18n="@@notes-help">
            Add free form notes to this account, e.g. why it was created.
          </span>
        </app-help-text>

        @if (create()) {
          <app-password-input
            label="Temporary Password" i18n-label="@@temporary-password"
            helpText="Set an initial temporary password for the user."
            i18n-helpText="@@temporary-password-help"
            [(password)]="password"
            [disabled]="!canEdit()"
            #passwordInput
          />
        }
      </div>

      <syn-button
        variant="filled"
        type="submit"
        [disabled]="!canSave(form)"
      >
        <syn-icon [name]="submitIcon() + '_fill'" slot="prefix" />
        <span>{{ submitText() }}</span>
      </syn-button>
    </form>
  `,
  styles: [],
  imports: [
    SynButtonComponent,
    SynIconComponent,
    SynInputComponent,
    SynCheckboxComponent,
    SynergyFormsModule,
    FormsModule,
    SynTextareaComponent,
    SynSelectComponent,
    SynOptionComponent,
    SynDividerComponent,
    PasswordInputComponent,
    SynValidateComponent,
    HelpTextComponent,
    SynAlertComponent,
  ],
})
export class CustomerAccountFormComponent {
  account = input.required<CustomerAccountInput>();
  ssus = input<string[]>([]);

  lastLogin = input<string | undefined>(undefined);
  lastEmailVerificationTriggered = input<string | undefined>(undefined);
  creationTimestamp = input<string | undefined>(undefined);

  username = signal('');
  email = signal('');

  canEdit = input(true);
  create = input(false);
  isAdmin = input(false);

  submitIcon = input('');
  submitText = input('');
  submitAccount = output<CustomerAccountInput>();

  password = signal('');
  ssuSelect = viewChild<SynSelectComponent>('ssuSelect');
  passwordInput = viewChild<PasswordInputComponent>('passwordInput');
  datePipe = inject(DatePipe);

  lastLoginFormatted = computed(() => {
    const raw = this.lastLogin();
    if (!raw) return '-';
    // Server returns "yyyy-MM-dd HH:mm:ss.SSS" (UTC) → convert to valid ISO-8601
    const isoString = raw.replace(' ', 'T') + 'Z';
    return this.datePipe.transform(isoString, 'medium') ?? raw;
  });

  lastEmailVerificationTriggeredFormatted = computed(() => {
    const raw = this.lastEmailVerificationTriggered();
    if (!raw) return '-';
    const ts = Number(raw);
    if (isNaN(ts)) return raw;
    return this.datePipe.transform(new Date(ts), 'medium') ?? raw;
  });

  creationTimestampFormatted = computed(() => {
    const raw = this.creationTimestamp();
    if (!raw) return '-';
    return this.datePipe.transform(raw, 'medium') ?? raw;
  });

  isSickEmployee = computed(() => {
    const split = this.username().split('@');
    if (split.length !== 2) {
      return false;
    }

    // SICK employee usernames have the form [first-name].[last-name]@sick.[top-level]
    // This is oversimplified but should catch almost all relevant cases.
    const name = split[0];
    const domain = split[1];
    return name.includes('.') && domain.startsWith('sick.');
  });

  triesToCreateSickEmployee = computed(() => {
    return this.create() && this.isSickEmployee();
  });

  // We have configured a maximum length of 150 characters for first name, last name, email, username
  // If we change the configuration, we might want to change this value here as well.
  maxPropertyLength = 150;

  constructor() {
    effect(() => {
      if (this.ssus().length > 0 && this.ssuSelect()) {
        // SSU value must be reset when all options are loaded to display the correct value
        this.ssuSelect()!.value = this.account().ssuCode ?? '';
      }

      const account = this.account();
      this.username.set(account.username);
      this.email.set(account.email);
    });
  }

  onSubmit(form: NgForm) {
    if (form.invalid) return;

    const account = { ...form.value };
    if (this.password().length > 0) {
      account.credentials = {
        password: this.password(),
        temporary: true,
      };
    }
    if (account.ssuCode === '') {
      account.ssuCode = undefined;
    }

    // Remove spaces from username and email to prevent copy & paste errors
    account.username = this.username().trim();
    account.email = this.email().trim();

    this.submitAccount.emit(account as CustomerAccountInput);
    form.form.markAsPristine();
  }

  canSave(form: NgForm) {
    if (!form.dirty) return false;
    if (form.invalid) return false;

    // Only if a temporary password is set: The password needs to be valid to create a new user
    if (this.passwordInput() && this.password().length > 0) {
      if (!this.passwordInput()!.isValid()) return false;
    }

    return this.canEdit();
  }
}
